import { api } from './api.js';
import { el } from './ui.js';
import { icon, type IconName } from './icons.js';
import type { LittleSurprise, SurpriseId, Surprises } from '../../shared/surprises.js';

type Mode = 'animated' | 'still' | 'off';
const MODE_KEY = 'memorie_tiziana_surprises_mode';
// L'aggancio è un attributo esplicito nella vista (`data-surprise-anchor`), non un selettore di
// struttura: un redesign che rinomina una classe faceva sparire il pensiero in silenzio.
const placements: Record<string, { id: SurpriseId; label: string; icon: IconName; after?: boolean }> = {
  home: { id: 'lettera', label: 'Psst… c’è una busta per te', icon: 'envelope' },
  libro: { id: 'segnalibro', label: 'Un segnalibro nascosto', icon: 'bookmark' },
  foto: { id: 'sorriso', label: 'C’è un sorriso tra le foto', icon: 'heart' },
  albero: { id: 'radici', label: 'Questo rametto è per te', icon: 'leaf' },
  ricette: { id: 'caffe', label: 'Ci prendiamo una pausa?', icon: 'cup', after: true },
};
export const anchorSelector = (route: string): string => `[data-surprise-anchor="${route}"]`;
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
let mode: Mode = 'animated';
try { const stored = localStorage.getItem(MODE_KEY); if (stored === 'still' || stored === 'off') mode = stored; } catch { /* preferenza in memoria */ }
let cached: Surprises | undefined, pending: Promise<Surprises | undefined> | null = null;
let generation = 0, dispose: (() => void) | null = null;

function load(): Promise<Surprises | undefined> {
  if (cached) return Promise.resolve(cached);
  if (!pending) pending = api.get<Surprises>('/api/surprises').then((value) => { cached = value; return value; }).catch(() => undefined).finally(() => { pending = null; });
  return pending;
}
const moves = (): boolean => mode === 'animated' && !reducedMotion.matches;

/** Non tocca il ciclo di vita dei registratori o degli editor. */
export function clearSurprises(): void {
  generation++;
  const cleanup = dispose; dispose = null; cleanup?.();
}

/** Decorazione facoltativa: una rete lenta o un errore non blocca la pagina. */
export async function mountSurprises(root: HTMLElement, route: string): Promise<void> {
  clearSurprises();
  const placement = placements[route];
  if (!placement && route !== 'impostazioni') return;
  const target = root.querySelector<HTMLElement>(anchorSelector(route));
  if (!target) {
    // Rumoroso di proposito: un pensiero che non compare più non lo scoprirebbe nessuno.
    console.error(`[sorprese] manca l’ancora data-surprise-anchor="${route}": il pensiero non viene mostrato`);
    return;
  }
  const current = generation;
  let config = await load();
  // Una GET della schermata precedente può essere appena stata annullata.
  if (!config && current === generation) config = await load();
  if (!config?.enabled || current !== generation || !target.isConnected) return;
  const nodes: HTMLElement[] = [];
  let closeGift: ((restoreFocus?: boolean) => void) | null = null;
  const remember = (node: HTMLElement): HTMLElement => { nodes.push(node); return node; };
  const updateMotion = (): void => { for (const node of nodes) node.dataset['motion'] = String(moves()); };
  const closeOnBack = (): void => closeGift?.();
  reducedMotion.addEventListener('change', updateMotion);
  window.addEventListener('popstate', closeOnBack);
  dispose = () => {
    closeGift?.(false); nodes.forEach((node) => node.remove());
    reducedMotion.removeEventListener('change', updateMotion);
    window.removeEventListener('popstate', closeOnBack);
  };

  if (route === 'impostazioni') {
    const feedback = el('p', { class: 'surprise-preference-status', role: 'status' });
    const options = el('fieldset', { class: 'surprise-preferences' }, el('legend', {}, `Le piccole sorprese di ${config.sender}`));
    for (const [value, label, hint] of [
      ['animated', 'Pensieri e animazioni', 'Piccoli movimenti solo quando apri una sorpresa.'],
      ['still', 'Solo i pensieri', 'Gli stessi messaggi, senza animazioni.'],
      ['off', 'Nascondi le sorprese', 'Il quaderno resta come prima.'],
    ] as const) {
      const input = el('input', { type: 'radio', name: 'surprise-mode', value, 'aria-label': label }); input.checked = mode === value;
      input.addEventListener('change', () => {
        if (!input.checked) return;
        mode = value;
        try { localStorage.setItem(MODE_KEY, value); feedback.textContent = 'Preferenza salvata su questo dispositivo.'; }
        catch { feedback.textContent = 'Preferenza applicata per questa visita. Il browser non permette di conservarla.'; }
        updateMotion();
      });
      options.append(el('label', {}, input, el('span', {}, el('strong', {}, label), el('small', {}, hint))));
    }
    const card = el('section', { class: 'carta surprise-settings', 'aria-label': 'Piccole sorprese' },
      options, el('p', { class: 'surprise-small' }, 'Solo su questo dispositivo. Nessun suono e nessun messaggio viene inviato. Se il dispositivo riduce le animazioni, lo rispettiamo sempre.'),
      el('details', {}, el('summary', {}, 'Un indizio su dove cercare'), el('p', {}, 'Una busta nella Casa, un segnalibro nel Libro, un sorriso nelle Foto, un rametto nell’Albero e due tazzine nelle Ricette.')), feedback);
    target.after(remember(card));
    return;
  }
  if (mode === 'off' || !placement) return;
  const note = config.notes.find((n) => n.id === placement.id); if (!note) return;
  const sender = config.sender, recipient = config.recipient;
  const trigger = el('button', { type: 'button', class: `surprise-trigger surprise-trigger-${note.id}`, 'aria-haspopup': 'dialog', 'aria-label': `${placement.label} · una sorpresa di ${sender}`, 'data-surprise': note.id },
    icon(placement.icon), el('span', {}, placement.label), el('span', { class: 'surprise-trigger-mark', 'aria-hidden': 'true' }, '♡'));
  if (placement.after) target.after(remember(trigger)); else target.append(remember(trigger));
  updateMotion();
  trigger.addEventListener('click', () => {
    if (closeGift || document.querySelector('dialog[open], [aria-modal="true"]')) return;
    const gift = openGift(note, sender, recipient, trigger, () => { closeGift = null; const index = nodes.indexOf(gift.dialog); if (index >= 0) nodes.splice(index, 1); });
    remember(gift.dialog); closeGift = gift.close; updateMotion();
  });
}

function scene(id: SurpriseId): HTMLElement {
  const stage = el('div', { class: `surprise-scene surprise-scene-${id}`, 'aria-hidden': 'true', 'data-step': '0' });
  if (id === 'lettera') stage.append(el('div', { class: 'surprise-letter' }, icon('heart')), el('div', { class: 'surprise-envelope' }, icon('envelope')));
  if (id === 'segnalibro') stage.append(el('div', { class: 'surprise-bookmark' }, el('span', { class: 'surprise-bookmark-front' }, icon('book')), el('span', { class: 'surprise-bookmark-back' }, icon('heart'))));
  if (id === 'sorriso') stage.append(el('div', { class: 'surprise-polaroid' }, el('div', { class: 'surprise-smile' }, el('span', {}, '•'), el('span', {}, '•'), el('i')), el('span', { class: 'surprise-polaroid-heart' }, '♡')));
  if (id === 'radici') stage.append(el('div', { class: 'surprise-stem' }), ...[0, 1, 2].map((i) => el('span', { class: `surprise-blossom surprise-blossom-${i}` }, icon('heart'))), icon('leaf', 'surprise-leaf'));
  if (id === 'caffe') stage.append(...['te', 'me'].map((who) => el('div', { class: 'surprise-cup' }, el('span', { class: 'surprise-steam' }, '∿'), icon('cup'), el('span', {}, who))));
  return stage;
}

function openGift(note: LittleSurprise, sender: string, recipient: string, trigger: HTMLElement, onClose: () => void): { dialog: HTMLDialogElement; close: (restoreFocus?: boolean) => void } {
  let closed = false, step = 0;
  let timer: number | undefined;
  const stage = scene(note.id);
  const response = el('p', { class: 'surprise-response', role: 'status', 'aria-live': 'polite', 'aria-atomic': 'true' });
  const toy = el('button', { type: 'button', class: 'surprise-play' }, note.action);
  if (note.id === 'segnalibro') toy.setAttribute('aria-pressed', 'false');
  const close = (restoreFocus = true): void => {
    if (closed) return;
    closed = true; window.clearTimeout(timer); dialog.close(); dialog.remove(); onClose();
    if (restoreFocus && trigger.isConnected) trigger.focus({ preventScroll: true });
  };
  const dialog = el('dialog', { class: `surprise-dialog surprise-dialog-${note.id}`, 'aria-labelledby': 'surprise-title', 'aria-describedby': 'surprise-intro', 'data-motion': String(moves()) },
    el('div', { class: 'surprise-dialog-top' }, el('p', {}, `PER MAMMA ${recipient.toLocaleUpperCase('it')}`), el('button', { type: 'button', class: 'surprise-close', 'aria-label': 'Chiudi il pensiero', click: () => close() }, '×')),
    el('div', { class: 'surprise-dialog-content' },
      el('p', { class: 'surprise-eyebrow' }, 'UN PENSIERO NASCOSTO NEL QUADERNO'),
      el('h2', { id: 'surprise-title', tabindex: '-1' }, note.title),
      el('div', { class: 'surprise-interaction' }, stage, toy, response,
        note.footnote ? el('p', { class: 'surprise-small' }, note.footnote) : null),
      el('div', { id: 'surprise-intro', class: 'surprise-message' }, ...note.paragraphs.map((p) => el('p', {}, p))),
      el('p', { class: 'surprise-signature' }, 'Con tutto il mio bene,', el('span', {}, sender)),
      el('button', { type: 'button', class: 'surprise-back', click: () => close() }, 'Torno al quaderno')));
  toy.addEventListener('click', () => {
    window.clearTimeout(timer);
    response.textContent = note.replies[step % note.replies.length]!; step++;
    stage.dataset['step'] = String(Math.min(3, step)); stage.classList.toggle('is-turned', step % 2 === 1);
    if (note.id === 'segnalibro') toy.setAttribute('aria-pressed', String(step % 2 === 1));
    stage.querySelector('.surprise-sparkles')?.remove();
    if (moves()) {
      const sparkles = el('span', { class: 'surprise-sparkles', 'aria-hidden': 'true' }, ...Array.from({ length: 6 }, (_, i) => el('i', { style: `--i:${i}` }, '♡')));
      stage.append(sparkles);
      timer = window.setTimeout(() => sparkles.remove(), 1400);
    }
  });
  dialog.addEventListener('cancel', (event) => { event.preventDefault(); close(); });
  dialog.addEventListener('click', (event) => {
    if (event.target !== dialog) return;
    const rect = dialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) close();
  });
  dialog.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab') return;
    const controls = [...dialog.querySelectorAll<HTMLButtonElement>('button')];
    if (event.shiftKey && (document.activeElement === controls[0] || document.activeElement?.id === 'surprise-title')) { event.preventDefault(); controls.at(-1)?.focus(); }
    else if (!event.shiftKey && document.activeElement === controls.at(-1)) { event.preventDefault(); controls[0]?.focus(); }
  });
  document.body.append(dialog); dialog.showModal(); dialog.querySelector<HTMLElement>('h2')?.focus({ preventScroll: true });
  return { dialog, close };
}
